/**
 * Admin Diagnostics — unified System Health & Diagnostics console.
 *
 * Registry-driven: every check rendered by the same <CheckCard>
 * component. Backend endpoint is /api/admin/diagnostics/run; report
 * export uses /api/admin/diagnostics/report.
 *
 * Legacy bespoke cards remain available behind `?legacy=1` for one
 * release while we verify parity.
 *
 * Auth: super-admin only (backend 404s non-super-admins).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import LiveBuildBadge from '@/components/LiveBuildBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, AlertOctagon, CheckCircle2, Loader2, RefreshCw,
  HelpCircle, ChevronDown, ChevronRight, Copy, Download, FileJson, FileText, Home,
} from 'lucide-react';
import AdminDiagnosticsLegacy from './AdminDiagnosticsLegacy';
import MigrationsCard from '@/components/diagnostics/MigrationsCard';
import DropboxRevokeCard from '@/components/diagnostics/DropboxRevokeCard';
import DropboxScopeCard from '@/components/diagnostics/DropboxScopeCard';
import ContactMigrationRollbackCard from '@/components/diagnostics/ContactMigrationRollbackCard';
import ContactGroupingRollbackCard from '@/components/diagnostics/ContactGroupingRollbackCard';
import ContactGroupingRunnerCard from '@/components/diagnostics/ContactGroupingRunnerCard';
import { YardDropInstrumentationPanel } from '@/components/YardDropInstrumentationPanel';
import ContactGroupingV2RunnerCard from '@/components/diagnostics/ContactGroupingV2RunnerCard';
import ContactGroupingV2RollbackCard from '@/components/diagnostics/ContactGroupingV2RollbackCard';
import ContactSyncMigrationsRunnerCard from '@/components/diagnostics/ContactSyncMigrationsRunnerCard';
import TestFixtureRunnerCard from '@/components/diagnostics/TestFixtureRunnerCard';
import MembershipSnapshotCard from '@/components/diagnostics/MembershipSnapshotCard';
import FixtureResetCard from '@/components/diagnostics/FixtureResetCard';
import FixtureSeedCard from '@/components/diagnostics/FixtureSeedCard';
import IdentityLinksCard from '@/components/diagnostics/IdentityLinksCard';
import RulebookLinkCard from '@/components/diagnostics/RulebookLinkCard';
import AccountTenantCheckCard from '@/components/diagnostics/AccountTenantCheckCard';
import ClipperInboxDebugCard from '@/components/diagnostics/ClipperInboxDebugCard';
import TenancyAuditCard from '@/components/diagnostics/TenancyAuditCard';
import ContactDuplicatesCard from '@/components/diagnostics/ContactDuplicatesCard';
import ContactCategoriesCard from '@/components/diagnostics/ContactCategoriesCard';
import EmailTemplatesUnifiedCard from '@/components/diagnostics/EmailTemplatesUnifiedCard';
import BackupCard from '@/components/admin/BackupCard';
import UnownedRowsCard from '@/components/admin/UnownedRowsCard';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  green:   { label: 'Healthy',   icon: CheckCircle2,  cls: 'bg-emerald-50 border-emerald-300 text-emerald-800' },
  amber:   { label: 'Drift',     icon: AlertTriangle, cls: 'bg-amber-50 border-amber-300 text-amber-900' },
  red:     { label: 'Issues',    icon: AlertOctagon,  cls: 'bg-rose-50 border-rose-300 text-rose-900' },
  unknown: { label: 'Info',      icon: HelpCircle,    cls: 'bg-slate-50 border-slate-300 text-slate-700' },
};

const STATUS_BADGE_CLS = {
  green:   'bg-emerald-100 text-emerald-900 border-emerald-300',
  amber:   'bg-amber-100 text-amber-900 border-amber-300',
  red:     'bg-rose-100 text-rose-900 border-rose-300',
  unknown: 'bg-slate-100 text-slate-700 border-slate-300',
};

const CATEGORY_TITLES = {
  data_integrity:    'Data Integrity',
  feature_health:    'Feature Health',
  security_posture:  'Security Posture',
  config_sanity:     'Config Sanity',
  deploy_state:      'Deploy State',
  live_services:     'Live Services',
};

const STALE_WARN_MIN = 10;

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function minutesAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}

export default function AdminDiagnostics() {
  const [params] = useSearchParams();
  if (params.get('legacy') === '1') {
    return <AdminDiagnosticsLegacy />;
  }
  return <UnifiedDiagnosticsConsole />;
}

function UnifiedDiagnosticsConsole() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPayload, setReportPayload] = useState(null);

  const fetchRun = useCallback(async ({ forceFresh = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API}/api/admin/diagnostics/run${forceFresh ? '?force_fresh=true' : ''}`;
      const res = await axios.get(url, { headers: authHeaders() });
      setSnapshot(res.data);
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Access denied — diagnostics are super-admin only.'
        : (err.response?.data?.detail || err.message || 'Run failed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial run on mount (cached, cheap — no force_fresh).
  useEffect(() => { fetchRun({ forceFresh: false }); }, [fetchRun]);

  const overall = snapshot?.overall || 'unknown';
  const overallTone = STATUS_TONE[overall];
  const OverallIcon = overallTone.icon;

  const resultsByCategory = useMemo(() => {
    const out = {};
    (snapshot?.results || []).forEach((r) => {
      (out[r.category] = out[r.category] || []).push(r);
    });
    return out;
  }, [snapshot]);

  const generatedAtMinAgo = minutesAgo(snapshot?.generated_at);
  const isStale = generatedAtMinAgo !== null && generatedAtMinAgo >= STALE_WARN_MIN;

  // Report generation
  const generateReport = useCallback(async ({ format }) => {
    setReportLoading(true);
    try {
      const headers = authHeaders();
      const url = `${API}/api/admin/diagnostics/report?format=${format}&force_fresh=false`;
      const res = await axios.get(url, {
        headers,
        responseType: format === 'markdown' ? 'text' : 'json',
        transformResponse: [(d) => d],  // disable axios JSON parse so md stays raw
      });
      let body = res.data;
      if (format === 'json' && typeof body === 'string') {
        try { body = JSON.stringify(JSON.parse(body), null, 2); } catch { /* leave raw */ }
      }
      setReportPayload({ format, body });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Report failed');
    } finally {
      setReportLoading(false);
    }
  }, []);

  const copyToClipboard = useCallback(async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Clipboard write failed — select the text manually');
    }
  }, []);

  const downloadFile = useCallback((text, ext) => {
    const blob = new Blob([text], { type: ext === 'json' ? 'application/json' : 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `key-made-health-${stamp}.${ext === 'json' ? 'json' : 'md'}`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="admin-diagnostics-page">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-sm mb-4" data-testid="breadcrumb-nav">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1 text-muted-foreground hover:text-[#BC5D41] transition-colors"
          data-testid="breadcrumb-my-desk"
        >
          <Home className="w-4 h-4" />
          My Desk
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <button
          onClick={() => navigate('/admin')}
          className="text-muted-foreground hover:text-[#BC5D41] transition-colors"
          data-testid="breadcrumb-admin"
        >
          Admin
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-[#1A1918]">Diagnostics</span>
      </nav>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A]">System Health &amp; Diagnostics</h1>
          <p className="text-slate-600 mt-1">
            Read-only checks across data integrity, security posture, feature health and deploy state.
            Manual refresh; results cached 60s.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => fetchRun({ forceFresh: false })}
            disabled={loading}
            data-testid="diagnostics-run-cached-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Run All
          </Button>
          <Button
            onClick={() => fetchRun({ forceFresh: true })}
            disabled={loading}
            data-testid="diagnostics-force-fresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Force Fresh
          </Button>
          <Button
            variant="outline"
            onClick={() => generateReport({ format: 'json' })}
            disabled={reportLoading}
            data-testid="diagnostics-report-json-btn"
          >
            <FileJson className="w-4 h-4 mr-2" /> JSON Report
          </Button>
          <Button
            variant="outline"
            onClick={() => generateReport({ format: 'markdown' })}
            disabled={reportLoading}
            data-testid="diagnostics-report-md-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> Markdown Report
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2">
          <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Dropbox · can this environment read file BYTES? Rendered OUTSIDE the
          snapshot gate on purpose: it is a one-button, self-contained probe and
          must stay reachable even when "Run All" times out. */}
      <DropboxScopeCard />

      {!snapshot && !error && (
        <div className="text-slate-500 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Running checks…
        </div>
      )}

      {snapshot && (
        <>
          {/* v7 (2026-07-12) — Super-admin fixture project reset */}
          <FixtureResetCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          <FixtureSeedCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          <IdentityLinksCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          {/* 2026-02 Account & Tenant Check — read-only tenant integrity probe */}
          <AccountTenantCheckCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          {/* 2026-02 Clipper Inbox (Debug) — read-only diagnostic for the browser-extension Staging inbox */}
          <ClipperInboxDebugCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          {/* 2026-02 Tenancy Audit — read-only map of orphans + unfiltered reads + unstamped writes */}
          <TenancyAuditCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          {/* 2026-02 Contact Duplicates — read-only report of duplicate `contacts` rows for Keystone */}
          <ContactDuplicatesCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          {/* 2026-02 Contact Categories — read-only enumeration of category-shaped fields on `contacts` */}
          <ContactCategoriesCard isSuperAdmin={snapshot?.is_super_admin || snapshot?.overall !== undefined} />
          <EmailTemplatesUnifiedCard />
          <RulebookLinkCard />
          {/* 2026-06 Database backup — the half Chad controls. Two buttons. */}
          <div className="mb-6"><BackupCard /></div>
          <div className="mb-6"><UnownedRowsCard /></div>
          {/* Overall + per-category tiles */}
          <div className={`p-5 rounded-xl border mb-6 ${overallTone.cls}`} data-testid="diagnostics-overall-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <OverallIcon className="w-7 h-7" />
                <div>
                  <div className="text-lg font-bold uppercase tracking-wide">
                    Overall: {overall}
                  </div>
                  <div className="text-xs opacity-80">
                    {snapshot.results.length} check{snapshot.results.length === 1 ? '' : 's'} ·
                    {' '}generated {generatedAtMinAgo !== null ? `${generatedAtMinAgo} min ago` : 'just now'}
                    {isStale && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px] font-semibold">
                        STALE &gt; {STALE_WARN_MIN}m — click Force Fresh
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {/* 2026-07-13 — full, uncached fingerprint + pod-start-time,
                  fetched LIVE from /api/health on every render. Owner
                  verifies deploys from this line; a 60s cache + a 12-char
                  truncation used to make prod look "up to date" for up to
                  a minute after an actual deploy. See LiveBuildBadge. */}
              <div className="text-xs text-right">
                <LiveBuildBadge />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {Object.entries(snapshot.category_status || {}).map(([cat, st]) => {
              const tone = STATUS_TONE[st] || STATUS_TONE.unknown;
              const Icon = tone.icon;
              const count = (resultsByCategory[cat] || []).length;
              return (
                <div
                  key={cat}
                  className={`p-3 rounded-lg border ${tone.cls}`}
                  data-testid={`diagnostics-cat-tile-${cat}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase">{st}</span>
                  </div>
                  <div className="text-sm font-semibold">{CATEGORY_TITLES[cat] || cat}</div>
                  <div className="text-xs opacity-80">{count} check{count === 1 ? '' : 's'}</div>
                </div>
              );
            })}
          </div>

          {/* Per-category check lists */}
          {Object.entries(resultsByCategory).map(([cat, items]) => (
            <Card key={cat} className="mb-4">
              <CardHeader>
                <CardTitle className="text-lg">{CATEGORY_TITLES[cat] || cat}</CardTitle>
                <CardDescription>{items.length} check{items.length === 1 ? '' : 's'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {[...items].sort((a, b) => STATUS_RANK(a.status) - STATUS_RANK(b.status))
                  .map((r) => <CheckCard key={r.id} result={r} onCopy={copyToClipboard} />)}
              </CardContent>
            </Card>
          ))}

          {/* Unified migration runner — replaces the Batch A/C/D token cards.
              Covers ALL migrations, current and future; super-admin + typed
              confirmation word. Old batch records live on the legacy view. */}
          <MigrationsCard onAfterApply={() => fetchRun({ forceFresh: true })} />

          {/* iter386c (D1) — Yard drop instrumentation panel.  Read-only. */}
          <YardDropInstrumentationPanel />

          {/* 2026-07-14 Security Follow-Up follow-up — Revoke leaked Dropbox
              shared links. Pair-runs with migration 0055. Super-admin only. */}
          <DropboxRevokeCard />

          {/* Phase 1 Contact Hub — restore canonical migration batches with
              typed-confirmation gating. Itself reversible via inverse_batch_id. */}
          <ContactMigrationRollbackCard />

          {/* Feb 2026 Contact Grouping Migration Runner — super-admin-only
              UI runner: Dry-Run (read-only) and Apply (typed-confirmation
              gated). Emits a CustomEvent on apply so the rollback card
              below auto-refreshes. */}
          <ContactGroupingRunnerCard />

          {/* Feb 2026 Contact Grouping — roll back a contact-grouping migration
              batch (restores company, clears group_id/needs_completion/
              data_hygiene_suspect, deletes batch-created groups, unlinks
              contractors). Tenant-scoped + typed-confirmation gated. */}
          <ContactGroupingRollbackCard />

          {/* Feb 2026 Contact Grouping V2 — single→multi migration runner
              (super-admin gated, typed-confirmation Apply). Emits
              'contact-grouping-v2-batch-applied' so the v2 rollback card
              below auto-refreshes. */}
          <ContactGroupingV2RunnerCard />
          <ContactGroupingV2RollbackCard />

          {/* Feb 2026 iter-291 — Contact-Sync Architecture migrations
              (0042 sync-targets seed, 0043 user-prefs fold-in, 0044
              Subcontractor category collapse). Each has its own
              Dry-Run / Apply / Rollback block. Super-admin only. */}
          <ContactSyncMigrationsRunnerCard />

          {/* Feb 2026 — Standing super-admin test fixture (sandbox project).
              Idempotent build/repair, reset-to-clean, and full removal.
              Invisible on every normal surface; lives only on the
              super-admin My Desk and in this card. */}
          <TestFixtureRunnerCard />

          {/* Membership Snapshot — read-only proof-of-state (Feb 2026 unification) */}
          <MembershipSnapshotCard />

          {/* Security · cross-tenant denial audit log (Phase 3) */}
          <SecurityDenialsCard />

          {/* Onboarding cleanup — email lookup + stray-tenant deactivation */}
          <OnboardingCleanupCard />

          {/* Pipeline stages — set Keystone's canonical 8-stage config */}
          <PipelineStagesCard onAfterApply={() => fetchRun({ forceFresh: true })} />

          {/* Migration drift inspector + amend */}
          <MigrationDriftCard onAfterApply={() => fetchRun({ forceFresh: true })} />

          {/* Comms test — email + SMS smoke test with cooldown */}
          <CommsTestCard />
        </>
      )}

      {reportPayload && (
        <ReportModal
          payload={reportPayload}
          onClose={() => setReportPayload(null)}
          onCopy={copyToClipboard}
          onDownload={downloadFile}
        />
      )}

      <div className="mt-8 text-xs text-slate-400">
        Need the old bespoke cards? <a href="?legacy=1" className="underline">View legacy view</a>.
      </div>
    </div>
  );
}

const STATUS_RANK = (st) => ({ red: 0, amber: 1, unknown: 2, green: 3 }[st] ?? 2);

/* ────────────────────────────────────────────────────────────────────────────
   PLAIN ENGLISH — the whole point of this page.

   Every check answers three questions in sentences: what it looks at, what it
   found with the real numbers spelled out, and what to do next. A check with no
   entry here SAYS SO rather than being guessed at, and its JSON stays where it
   always was, behind the closed "Detail (JSON)" toggle.

   `kind` is what a red MEANS, and the three are not the same thing:
     LOST DATA    — something is gone or unreachable and cannot be re-made
     PRIVACY RISK — one firm could see another firm's information
     BOOKKEEPING  — the records disagree with each other; nothing is lost
   ──────────────────────────────────────────────────────────────────────────── */
const KIND_LABEL = {
  lost: { text: 'LOST DATA', cls: 'bg-rose-100 text-rose-900 border-rose-300' },
  privacy: { text: 'PRIVACY RISK', cls: 'bg-purple-100 text-purple-900 border-purple-300' },
  books: { text: 'BOOKKEEPING', cls: 'bg-sky-100 text-sky-900 border-sky-300' },
};

const n = (v) => (typeof v === 'number' ? v : 0);
const plural = (c, one, many) => `${c} ${c === 1 ? one : many}`;

const PLAIN_ENGLISH = {
  'security_posture.boot_guard': {
    kind: 'privacy',
    checks: 'Whether every place in the app that reads your records first asks which firm owns them.',
    found: (d) => (d.clean
      ? 'Every read asks which firm owns the records.'
      : `${plural(n(d.offender_count), 'place', 'places')} read data without checking which firm owns it.`),
    todo: (d) => (d.clean
      ? 'Nothing, this is healthy.'
      : 'This is the one to fix first. Until it is fixed, one firm could be shown another firm’s records.'),
  },
  'security_posture.no_public_cache_on_protected_files': {
    kind: 'privacy',
    checks: 'Whether private files could be kept in a public cache where anyone could fetch them again.',
    found: (d) => `${plural(n(d.scanned_files), 'file-serving route was', 'file-serving routes were')} checked and none allows public caching.`,
    todo: () => 'Nothing, this is healthy.',
  },
  'security_posture.no_stale_thumb_param': {
    kind: 'books',
    checks: 'Whether image links still carry an old size instruction that no longer does anything.',
    found: (d) => `${plural(n(d.scanned_files), 'file was', 'files were')} searched and none carries the old instruction.`,
    todo: () => 'Nothing, this is healthy.',
  },
  'data_integrity.orphan_writes': {
    kind: 'books',
    checks: 'Whether any record was saved without recording which firm it belongs to.',
    found: (d) => {
      const c = d.counts || {};
      const bad = Object.entries(c).filter(([, v]) => n(v) > 0);
      return bad.length === 0
        ? 'Every record carries the firm that owns it.'
        : `${bad.map(([k, v]) => `${plural(n(v), 'record', 'records')} in ${k.replace(/_/g, ' ')}`).join(', ')} — saved with no firm recorded.`;
    },
    todo: (d) => {
      const bad = Object.entries(d.counts || {}).filter(([, v]) => n(v) > 0);
      return bad.length === 0
        ? 'Nothing, this is healthy.'
        : 'Nothing is lost. These records need the owning firm stamped on them before they can be filtered properly.';
    },
  },
  'data_integrity.crm_leads': {
    kind: 'books',
    checks: 'Whether every lead sits on a pipeline stage that still exists in your settings.',
    found: (d) => `${plural(n(d.total_leads), 'lead', 'leads')} in total. `
      + `${n(d.leads_without_company_id) === 0 ? 'All carry a firm.' : `${n(d.leads_without_company_id)} carry no firm.`} `
      + `They sit on: ${(d.observed_stages || []).join(', ') || 'no stage at all'}. `
      + `Your settings list: ${(d.configured_stages || []).join(', ')}.`,
    todo: () => 'Compare the two lists above. A lead on a stage that is not in your settings will not appear on the board.',
  },
  'data_integrity.contacts_legacy': {
    kind: 'books',
    checks: 'Whether any contact is still stored as one run-together name instead of a first and last name.',
    found: (d) => `${plural(n(d.legacy_name_count), 'contact is', 'contacts are')} still stored as a single name.`,
    todo: (d) => (n(d.legacy_name_count) === 0
      ? 'Nothing, this is healthy.'
      : 'Nothing is lost. Open each one and put the first and last name in their own boxes so sorting and searching work.'),
  },
  'data_integrity.identity_invariants': {
    kind: 'books',
    checks: 'Whether every contractor has a matching contact card, and every contact belongs to a real project.',
    found: (d) => `${n(d.orphan_contractors_total)} contractors without a contact card, `
      + `${n(d.orphan_contacts_total)} contacts without an owner, `
      + `${n(d.project_mismatches_total)} where the two disagree about the project.`,
    todo: (d) => (n(d.orphan_contractors_total) + n(d.orphan_contacts_total) + n(d.project_mismatches_total) === 0
      ? 'Nothing, this is healthy.'
      : 'Nothing is lost. Each pair needs re-linking.'),
  },
  'data_integrity.rulebook_staleness': {
    kind: 'books',
    checks: 'Whether the Rule Book still describes the app as it is now, or describes screens that have been renamed or moved.',
    found: (d) => `${plural(n(d.scanned_features), 'feature was', 'features were')} compared and `
      + `${plural(n(d.stale_count), 'description is', 'descriptions are')} out of date`
      + ((d.stale || []).length ? `: ${(d.stale || []).map((s) => `“${s.value}”`).join(', ')}.` : '.'),
    todo: (d) => (n(d.stale_count) === 0
      ? 'Nothing, this is healthy.'
      : 'Nothing is lost and nothing is broken for anyone using the app. The Rule Book’s wording needs updating to match the screens named above.'),
  },
  'data_integrity.no_raw_dropbox_urls': {
    kind: 'lost',
    checks: 'Whether any record stores a Dropbox web address directly, which stops working when the link is changed.',
    found: (d) => `${plural(n(d.scanned_fields), 'place was', 'places were')} searched and ${n(d.total_raw_urls)} raw Dropbox addresses were found.`,
    todo: (d) => (n(d.total_raw_urls) === 0 ? 'Nothing, this is healthy.' : 'Each of these needs re-saving through the app so the file can still be found later.'),
  },
  'data_integrity.image_doctor': {
    kind: 'lost',
    checks: 'Whether every project picture and every profile photo can actually be fetched.',
    found: (d) => `Of ${plural(n(d.total_projects), 'project', 'projects')}, ${n(d.projects_with_image)} have a picture and ${n(d.projects_broken)} of those cannot be fetched. `
      + `Of ${plural(n(d.avatar_urls_scanned), 'profile photo', 'profile photos')}, ${n(d.avatars_broken)} cannot be fetched.`,
    todo: (d) => (n(d.projects_broken) + n(d.avatars_broken) === 0
      ? 'Nothing, this is healthy.'
      : 'These pictures are gone from storage. The people concerned need to upload them again — there is nothing to repair.'),
  },
  'data_integrity.db_backup': {
    kind: 'lost',
    checks: 'Whether last night’s backup was taken, is encrypted, and can be restored.',
    found: (d) => {
      const b = d.latest_backup || {};
      const age = b.age_hours;
      return `The most recent backup was taken ${age == null ? 'at an unknown time' : `${Math.round(age)} hours ago`}`
        + `${b.taken_at ? ` (${String(b.taken_at).slice(0, 16).replace('T', ' ')})` : ''}. `
        + `It is encrypted, and the app itself has no way to delete a backup. `
        + `Kept: ${d.retention_policy || 'unknown'}.`;
    },
    todo: (d) => {
      const age = (d.latest_backup || {}).age_hours;
      return age != null && age > 26
        ? `No backup has been taken for ${Math.round(age)} hours. Backups should be daily, so find out why last night’s did not run before anything else changes.`
        : 'Nothing, this is healthy.';
    },
  },
  'data_integrity.drawing_file_doctor': {
    kind: 'lost',
    checks: 'Whether every drawing in the register points at a real file that is really a drawing.',
    found: (d) => {
      const c = d.cause_counts || {};
      const words = {
        // gerunds, so the sentence reads correctly whether the count is 1 or 111
        no_file: 'with no file attached at all',
        not_found_dropbox: 'pointing at a file that is no longer in Dropbox',
        raw_dropbox_url_still_stored: 'still storing a Dropbox web address instead of going through the app',
        wrong_bytes_not_pdf: 'pointing at something that is not a drawing',
        external_junk_url: 'pointing at an address outside the app entirely',
      };
      const parts = Object.entries(c).filter(([, v]) => n(v) > 0)
        .map(([k, v]) => `${v} ${words[k] || k.replace(/_/g, ' ')}`);
      return `Of ${plural(n(d.total), 'drawing', 'drawings')}, ${n(d.ok)} open correctly and ${n(d.failing)} do not. `
        + (parts.length ? `${parts.join('; ')}.` : '');
    },
    todo: (d) => {
      const c = d.cause_counts || {};
      const raw = n(c.raw_dropbox_url_still_stored);
      return n(d.failing) === 0
        ? 'Nothing, this is healthy.'
        : (raw > 0
          ? `Most of these — ${raw} — are not lost: they store a Dropbox address that needs re-saving through the app. The ones with no file at all, or pointing at something that is not a drawing, will have to be uploaded again.`
          : 'These drawings need re-uploading; the files they point at cannot be read.');
    },
    list: (d) => (d.rows || []).map((r) => ({
      name: [r.drawing_number, r.title].filter(Boolean).join(' — ') || '(untitled drawing)',
      note: {
        no_file: 'no file attached',
        not_found_dropbox: 'file missing from Dropbox',
        raw_dropbox_url_still_stored: 'stores a Dropbox address directly',
        wrong_bytes_not_pdf: 'not a drawing',
        external_junk_url: 'points outside the app',
      }[r.cause] || r.cause,
    })),
  },
  'deploy_state.migrations': {
    kind: 'books',
    checks: 'Whether every database update that ships with the app has actually been run.',
    found: (d) => `${plural(n(d.applied), 'update has', 'updates have')} been run and ${plural(n(d.pending), 'is', 'are')} still waiting`
      + (n(d.file_drift) ? `, and ${plural(n(d.file_drift), 'file has', 'files have')} changed since it was run.` : '.'),
    todo: (d) => (n(d.pending) === 0
      ? 'Nothing, this is healthy.'
      : 'Nothing is lost. Read the pre-flight check below this one first — it says what each waiting update would change — then decide which to run.'),
    list: (d) => (d.pending_names || []).map((name) => ({ name, note: 'waiting' })),
  },
  'deploy_state.migrations_preflight': {
    kind: 'books',
    checks: 'What each waiting database update would actually change, before any of them is run.',
    found: (d) => `${plural(n(d.pending_count), 'update is', 'updates are')} waiting. Between them they would `
      + `create ${n(d.index_will_create)} indexes, change ${n(d.backfill_would_update)} records, `
      + `re-link ${n(d.parent_chain_would_resolve)} and file away ${n(d.parent_chain_would_archive)}.`,
    todo: (d) => (n(d.backfill_would_update) + n(d.index_will_create) === 0
      ? 'Nothing, this is healthy — the waiting updates would change no records.'
      : 'Read the numbers above before running anything.'),
  },
  'deploy_state.version_skew': {
    kind: 'books',
    checks: 'Whether the version running right now is the version that was last built.',
    found: (d) => `Running version ${d.running_app_version || 'unknown'}, build ${d.running_git_commit || 'unknown'}, `
      + `started ${d.process_started_at ? String(d.process_started_at).slice(0, 16).replace('T', ' ') : 'at an unknown time'}.`,
    todo: () => 'Nothing, this is healthy.',
  },
  'feature_health.content_completeness': {
    kind: 'books',
    checks: 'Whether every project’s phases and steps were filled in properly when it was set up.',
    found: (d) => {
      const j = d.journey || {};
      const l = d.legacy_tasks || {};
      return `${plural(n(j.total_projects_audited), 'project was', 'projects were')} checked and `
        + `${plural(n(j.projects_with_issues), 'has', 'have')} something missing — ${plural(n(j.issue_count), 'gap', 'gaps')} in all`
        + `${j.issues_capped_at_200 ? ' (counting stopped at 200)' : ''}. `
        + `${n(l.count)} steps are still in the old format.`;
    },
    todo: (d) => (n((d.journey || {}).projects_with_issues) === 0
      ? 'Nothing, this is healthy.'
      : 'Nothing is lost. Open the projects named in the detail and fill in the missing phases or steps.'),
  },
  'live_services.email_send': {
    kind: 'lost',
    checks: 'Whether the app can actually send an email right now, by sending a real test one.',
    found: (d) => (d.error
      ? `The email service ${d.provider || ''} refused the attempt and said: “${d.error}”. No email was sent.`
      : `A test email was sent successfully through ${d.provider || 'the email service'}.`),
    todo: (d) => (d.error
      ? 'No email is leaving the app — not reminders, not client notifications, not portal invitations, and nobody is being told. The email service’s key needs renewing.'
      : 'Nothing, this is healthy.'),
  },
  'live_services.llm_keys': {
    kind: 'books',
    checks: 'Whether AI Chad can reach the model it depends on, by asking it a real question.',
    found: (d) => `${d.model || 'The model'} answered through ${d.provider_path || 'its provider'}.`,
    todo: () => 'Nothing, this is healthy.',
  },
  'live_services.sms_send': {
    kind: 'books',
    checks: 'Whether the text-message account is live and has a number to send from. No text is actually sent.',
    found: (d) => `The ${d.provider || 'SMS'} account “${d.account_friendly_name || 'unknown'}” is ${d.account_status || 'in an unknown state'}`
      + `${d.from_number ? `, sending from ${d.from_number}` : ', with no sending number set'}.`,
    todo: (d) => (d.account_status === 'active' ? 'Nothing, this is healthy.' : 'The text-message account is not active. Nothing will send until it is.'),
  },
  'live_services.dropbox_content_scope': {
    kind: 'lost',
    checks: 'Whether the app can still read the contents of files in your Dropbox, not just list them.',
    found: (d) => `Connected to the folder ${d.upload_folder || 'unknown'}; `
      + `signing in ${d.users_get_current_account || 'unknown'}, searching ${d.files_search || 'unknown'}.`,
    todo: (d) => (d.is_storage_configured ? 'Nothing, this is healthy.' : 'Dropbox is not connected. Files will not save to it.'),
  },
};

function PlainEnglish({ result }) {
  const entry = PLAIN_ENGLISH[result.id];
  const healthy = result.status === 'green';
  if (!entry) {
    return (
      <div className="px-3 pb-3 pt-0" data-testid={`diagnostics-plain-missing-${result.id}`}>
        <div className="p-2 rounded border border-amber-300 bg-amber-50 text-xs text-amber-900">
          No plain description written for this check. Its detail is in the “Detail (JSON)”
          toggle below, unchanged.
        </div>
      </div>
    );
  }
  const d = result.detail || {};
  let found;
  let todo;
  try { found = entry.found(d); } catch { found = null; }
  try { todo = entry.todo(d); } catch { todo = null; }
  const rows = (!healthy && entry.list) ? (() => { try { return entry.list(d) || []; } catch { return []; } })() : [];
  const kind = (!healthy && entry.kind) ? KIND_LABEL[entry.kind] : null;

  return (
    <div className="px-3 pb-3 pt-0 space-y-1.5 text-xs" data-testid={`diagnostics-plain-${result.id}`}>
      {kind && result.status === 'red' && (
        <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-bold tracking-wide ${kind.cls}`}
              data-testid={`diagnostics-kind-${result.id}`}>
          {kind.text}
        </span>
      )}
      <p className="text-slate-500"><span className="font-semibold text-slate-700">What it checks: </span>{entry.checks}</p>
      <p className="text-slate-700"><span className="font-semibold">What it found: </span>
        {found || 'This check returned something the page could not put into words. Its detail is in the toggle below.'}
      </p>
      <p className={result.status === 'green' ? 'text-emerald-800' : 'text-slate-900 font-medium'}>
        <span className="font-semibold">What to do: </span>
        {todo || 'Open the detail below.'}
      </p>
      {rows.length > 0 && (
        <details data-testid={`diagnostics-plain-list-${result.id}`}>
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900 select-none">
            The {rows.length} it names {rows.length < n(d.failing || d.pending || rows.length) ? `(of ${n(d.failing || d.pending)}; the check only returns a sample)` : ''}
          </summary>
          <ul className="mt-1 ml-4 list-disc space-y-0.5 text-slate-700">
            {rows.map((r, i) => (
              <li key={i}>{r.name}{r.note ? ` — ${r.note}` : ''}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function CheckCard({ result, onCopy }) {
  const [expanded, setExpanded] = useState(result.status === 'red' || result.status === 'amber');
  const tone = STATUS_TONE[result.status] || STATUS_TONE.unknown;
  const Icon = tone.icon;
  const badgeCls = STATUS_BADGE_CLS[result.status] || STATUS_BADGE_CLS.unknown;
  const cached = result.from_cache;

  return (
    <div
      className="border border-slate-200 rounded-lg bg-white overflow-hidden"
      data-testid={`diagnostics-check-${result.id}`}

    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50 transition-colors"
        data-testid={`diagnostics-check-toggle-${result.id}`}
      >
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${result.status === 'green' ? 'text-emerald-600' : result.status === 'amber' ? 'text-amber-600' : result.status === 'red' ? 'text-rose-600' : 'text-slate-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{result.title}</span>
            <Badge className={badgeCls}>{result.status.toUpperCase()}</Badge>
            {cached && (
              <Badge variant="outline" className="text-[10px] text-slate-500">cached</Badge>
            )}
            <code className="text-[10px] text-slate-400 font-mono">{result.id}</code>
          </div>
          <div className="text-xs text-slate-600 mt-0.5">{result.summary}</div>
        </div>
        <div className="shrink-0 text-slate-400 mt-0.5">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>

      {/* Always visible, never behind a toggle: the three sentences. The JSON
          stays exactly where it was, in the closed "Detail (JSON)" toggle. */}
      <PlainEnglish result={result} />

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2 text-xs" data-testid={`diagnostics-check-detail-${result.id}`}>
          {result.suggested_fix && (
            <div className="p-2 rounded bg-white border border-slate-200">
              <div className="font-semibold text-slate-700 mb-0.5">Suggested fix</div>
              <div className="text-slate-700">{result.suggested_fix}</div>
            </div>
          )}
          {result.docs_ref && (
            <div className="text-slate-600">
              <span className="font-semibold">Docs:</span> <code>{result.docs_ref}</code>
            </div>
          )}
          {result.error && (
            <div className="p-2 rounded bg-rose-50 border border-rose-200 text-rose-800 font-mono">
              <strong>Probe error:</strong> {result.error}
            </div>
          )}
          <details>
            <summary className="cursor-pointer text-slate-600 hover:text-slate-900 select-none">
              Detail (JSON)
            </summary>
            <pre className="mt-1 p-2 bg-slate-900 text-slate-100 rounded overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(result.detail || {}, null, 2)}
            </pre>
          </details>
          <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1">
            <span>Probed {result.probed_at} · {result.duration_ms} ms</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-[10px]"
              onClick={() => onCopy(JSON.stringify(result, null, 2), 'Check JSON')}
              data-testid={`diagnostics-check-copy-${result.id}`}
            >
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportModal({ payload, onClose, onCopy, onDownload }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="diagnostics-report-modal"
    >
      <div
        className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">
              Health Report · {payload.format.toUpperCase()}
            </h2>
            <p className="text-xs text-slate-600">
              Paste to your AI advisor or save as a file. Read-only snapshot — no secrets included.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCopy(payload.body, payload.format === 'json' ? 'JSON' : 'Markdown')}
              data-testid="diagnostics-report-copy-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDownload(payload.body, payload.format)}
              data-testid="diagnostics-report-download-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Download
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
        <pre
          className="flex-1 overflow-auto p-4 m-0 bg-slate-900 text-slate-100 text-xs whitespace-pre-wrap font-mono"
          data-testid="diagnostics-report-body"
        >
{payload.body}
        </pre>
      </div>
    </div>
  );
}


function CommsTestCard() {
  const [open, setOpen] = useState(false);
  const [stamps, setStamps] = useState({});
  const [adminEmail, setAdminEmail] = useState('');
  const [emailTarget, setEmailTarget] = useState('');
  const [smsTarget, setSmsTarget] = useState('');
  const [sending, setSending] = useState(null);          // 'email' | 'sms' | null
  const [results, setResults] = useState({});            // channel -> { ok, text }
  const [cooldowns, setCooldowns] = useState({});        // channel -> seconds remaining

  const loadStamps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/diagnostics/comms-test`, { headers: authHeaders() });
      setStamps(res.data.stamps || {});
      // Resume cooldown countdowns across reloads
      const next = {};
      ['email', 'sms'].forEach((ch) => {
        const at = res.data.stamps?.[ch]?.tested_at;
        if (at) {
          const left = Math.ceil((res.data.cooldown_seconds || 60) - (Date.now() - new Date(at).getTime()) / 1000);
          if (left > 0) next[ch] = left;
        }
      });
      setCooldowns(next);
    } catch { /* not super-admin or endpoint unavailable — card stays empty */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadStamps();
    axios.get(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((res) => setAdminEmail(res.data?.email || res.data?.user?.email || ''))
      .catch(() => {});
  }, [open, loadStamps]);

  // Tick the countdowns
  useEffect(() => {
    const hasActive = Object.values(cooldowns).some((v) => v > 0);
    if (!hasActive) return undefined;
    const t = setInterval(() => {
      setCooldowns((prev) => {
        const next = {};
        Object.entries(prev).forEach(([ch, v]) => { if (v > 1) next[ch] = v - 1; });
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [cooldowns]);

  const sendTest = useCallback(async (channel, target) => {
    if (!target.trim()) {
      toast.error('Type the recipient first — it is never pre-filled');
      return;
    }
    setSending(channel);
    setResults((prev) => ({ ...prev, [channel]: null }));
    try {
      const res = await axios.post(
        `${API}/api/admin/diagnostics/comms-test`,
        { channel, target: target.trim() },
        { headers: authHeaders() },
      );
      setResults((prev) => ({ ...prev, [channel]: { ok: res.data.success, text: res.data.result } }));
      setStamps((prev) => ({ ...prev, [channel]: res.data.stamp }));
      setCooldowns((prev) => ({ ...prev, [channel]: res.data.cooldown_seconds || 60 }));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setResults((prev) => ({ ...prev, [channel]: { ok: false, text: detail } }));
      if (err.response?.status === 429) {
        const m = /wait (\d+)s/.exec(detail);
        if (m) setCooldowns((prev) => ({ ...prev, [channel]: parseInt(m[1], 10) }));
      }
    } finally {
      setSending(null);
    }
  }, []);

  const renderChannel = (channel, label, target, setTarget, placeholder, inputType) => {
    const cd = cooldowns[channel] || 0;
    const result = results[channel];
    const stamp = stamps[channel];
    return (
      <div className="p-3 rounded-lg border border-slate-200 bg-white space-y-2" data-testid={`comms-test-${channel}`}>
        <div className="font-semibold text-sm">{label}</div>
        <div className="flex gap-2">
          <input
            type={inputType}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder={placeholder}
            className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
            data-testid={`comms-test-${channel}-input`}
          />
          <Button
            size="sm"
            disabled={sending === channel || cd > 0}
            onClick={() => sendTest(channel, target)}
            data-testid={`comms-test-${channel}-send-btn`}
          >
            {sending === channel ? 'Sending…' : cd > 0 ? `Wait ${cd}s` : 'Send Test'}
          </Button>
        </div>
        {result && (
          <div
            className={`text-xs p-2 rounded border font-mono ${result.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}
            data-testid={`comms-test-${channel}-result`}
          >
            {result.text}
          </div>
        )}
        <div className="text-[11px] text-slate-500" data-testid={`comms-test-${channel}-stamp`}>
          {stamp
            ? <>Last {channel} test: {new Date(stamp.tested_at).toLocaleString()} — {stamp.result}</>
            : <>No {channel} test recorded yet</>}
        </div>
      </div>
    );
  };

  return (
    <Card className="mb-4" data-testid="comms-test-card">
      <CardHeader className="cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <CardTitle className="text-lg flex items-center justify-between">
          Comms Test
          <span className="text-slate-400 text-sm">{open ? '▾' : '▸'}</span>
        </CardTitle>
        <CardDescription>
          Send a clearly-labeled test email / SMS through the live providers. 60s cooldown per
          channel. Recipients are typed each time — never pre-filled from client data.
        </CardDescription>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          {renderChannel('email', 'Email test (Resend)', emailTarget, setEmailTarget,
            adminEmail || 'you@yourcompany.com', 'email')}
          {renderChannel('sms', 'SMS test (Twilio)', smsTarget, setSmsTarget,
            '+1 555 000 0000', 'tel')}
        </CardContent>
      )}
    </Card>
  );
}

function OnboardingCleanupCard() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupData, setLookupData] = useState(null);
  const [error, setError] = useState(null);
  const [actionToken, setActionToken] = useState('');
  const [actionResult, setActionResult] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const doLookup = useCallback(async () => {
    setError(null);
    setActionResult(null);
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/diagnostics/user-lookup`, {
        headers: authHeaders(),
        params: { email },
      });
      setLookupData(res.data);
    } catch (err) {
      setLookupData(null);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLookupLoading(false);
    }
  }, [email]);

  const deactivateUser = useCallback(async (user_id, company_id) => {
    if (!window.confirm(`Deactivate user ${user_id} in company ${company_id}?\n\nThis sets is_active=false (does NOT delete). Frees the email so a fresh invite can be accepted.`)) return;
    setError(null);
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/diagnostics/deactivate-stray-user`, null, {
        headers: { ...authHeaders(), 'X-Admin-Migrations-Token': actionToken },
        params: { user_id, company_id },
      });
      setActionResult({ kind: 'deactivate', data: res.data });
      toast.success('User deactivated — email freed');
      await doLookup();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setActionLoading(false);
    }
  }, [actionToken, doLookup]);

  const archiveCompany = useCallback(async (company_id, expected_users) => {
    if (!window.confirm(`Archive + delete company ${company_id}?\n\nMoves the row to _orphaned_rows_2026_06_10 then deletes from companies. Refuses if any active users / projects / leads remain.`)) return;
    setError(null);
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/diagnostics/archive-stray-company`, null, {
        headers: { ...authHeaders(), 'X-Admin-Migrations-Token': actionToken },
        params: { company_id, expected_footprint_users: expected_users },
      });
      setActionResult({ kind: 'archive', data: res.data });
      toast.success('Company archived');
      await doLookup();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setActionLoading(false);
    }
  }, [actionToken, doLookup]);

  return (
    <Card className="mb-4 border-amber-200" data-testid="onboarding-cleanup-card">
      <CardHeader>
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center justify-between w-full text-left" data-testid="onboarding-cleanup-toggle">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Onboarding · email lookup &amp; stray-tenant cleanup
            </CardTitle>
            <CardDescription>
              Cross-tenant lookup for users/companies/invitations by email. Soft-deactivate (sets <code>is_active=false</code>) and archive-then-delete stray empty tenants.
            </CardDescription>
          </div>
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm"
                data-testid="onboarding-email-input"
              />
              <Button onClick={doLookup} disabled={!email || lookupLoading} data-testid="onboarding-lookup-btn">
                {lookupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Look up
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Action token (required for deactivate / archive — NOT for lookup)
            </label>
            <input
              type="password"
              value={actionToken}
              onChange={(e) => setActionToken(e.target.value)}
              placeholder="StrayTenant cleanup token"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono"
              data-testid="onboarding-action-token-input"
            />
          </div>

          {error && (
            <div className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800 flex items-start gap-2">
              <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          {lookupData && (
            <div className="space-y-3" data-testid="onboarding-lookup-result">
              <div className="text-sm font-semibold text-slate-800">
                {lookupData.email} · {lookupData.summary.user_count} user(s) · {lookupData.summary.active_user_count} active · {lookupData.summary.company_count} company(s) · {lookupData.summary.invitation_count} invitation(s)
              </div>

              {lookupData.users.length === 0 ? (
                <div className="text-xs text-emerald-700 p-3 rounded bg-emerald-50 border border-emerald-200">
                  No users with this email. Email is free — a fresh invite can be accepted immediately.
                </div>
              ) : lookupData.users.map((u) => {
                const company = lookupData.companies.find((c) => c.id === u.company_id);
                const fp = lookupData.company_footprints?.[u.company_id] || {};
                return (
                  <div key={u.id} className="p-3 rounded border border-slate-200 bg-white text-xs">
                    <div className="font-semibold mb-1">
                      <Badge className={u.is_active ? STATUS_BADGE_CLS.amber : STATUS_BADGE_CLS.green}>
                        {u.is_active ? 'active' : 'inactive'}
                      </Badge>
                      <span className="ml-2 font-mono">user_id: {u.id}</span>
                    </div>
                    <div className="text-slate-700 space-y-0.5">
                      <div>company_id: <code>{u.company_id}</code> {company ? `(name: "${company.name}")` : '(NO MATCHING COMPANY ROW)'}</div>
                      <div>role: <code>{u.role}</code> · created: {u.created_at}</div>
                      <div className="text-slate-500">
                        Footprint of this tenant:{' '}
                        users={fp.users} (active={fp.active_users}),
                        projects={fp.projects}, crm_leads={fp.crm_leads},
                        invoices={fp.invoices}, invitations={fp.team_invitations}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {u.is_active && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deactivateUser(u.id, u.company_id)}
                          disabled={!actionToken || actionLoading}
                          data-testid={`onboarding-deactivate-user-${u.id}`}
                        >
                          Deactivate user (soft — sets is_active=false)
                        </Button>
                      )}
                      {company && fp.active_users === 0 && fp.projects === 0 && fp.crm_leads === 0 && fp.invoices === 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => archiveCompany(u.company_id, fp.users)}
                          disabled={!actionToken || actionLoading}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                          data-testid={`onboarding-archive-company-${u.company_id}`}
                        >
                          Archive + delete this company
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              {lookupData.invitations.length > 0 && (
                <div className="p-3 rounded border border-slate-200 bg-slate-50">
                  <div className="text-xs font-semibold mb-1">Invitations for this email ({lookupData.invitations.length})</div>
                  <ul className="text-[11px] font-mono space-y-1">
                    {lookupData.invitations.map((inv) => (
                      <li key={inv.id} className="flex flex-wrap items-center gap-2">
                        <Badge className={inv.status === 'pending' ? STATUS_BADGE_CLS.amber : inv.status === 'revoked' ? STATUS_BADGE_CLS.red : STATUS_BADGE_CLS.green}>
                          {inv.status}
                        </Badge>
                        <span>cid: {inv.company_id?.slice(0, 12)}…</span>
                        <span>role: {inv.role}</span>
                        <span className="text-slate-500">created: {inv.created_at}</span>
                        <span className="text-slate-500">expires: {inv.expires_at}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <details>
                <summary className="cursor-pointer text-xs text-slate-600">Raw JSON</summary>
                <pre className="mt-1 p-2 bg-slate-900 text-slate-100 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(lookupData, null, 2)}
                </pre>
              </details>
            </div>
          )}

          {actionResult && (
            <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-xs">
              <div className="font-semibold mb-1">Action complete · {actionResult.kind}</div>
              <pre className="bg-slate-900 text-slate-100 rounded p-2 text-[10px] overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(actionResult.data, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}


function PipelineStagesCard({ onAfterApply }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchCurrent = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/diagnostics/pipeline-stages`, { headers: authHeaders() });
      setCurrent(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open && !current) fetchCurrent(); }, [open, current, fetchCurrent]);

  const apply = useCallback(async () => {
    if (!window.confirm('Overwrite pipeline_config with the canonical Keystone 8-stage list?\n\nInquiry → Consultation → Proposal → Design → Contract → Construction → Closeout → Lost.\n\nReversible via the existing PUT /api/pipeline/stages or by re-running with a different payload.')) return;
    setError(null);
    setApplying(true);
    try {
      const res = await axios.post(`${API}/api/admin/diagnostics/pipeline-stages/set-keystone-canonical`, null, { headers: authHeaders() });
      setResult(res.data);
      toast.success('Pipeline stages set to Keystone canonical (7 stages)');
      await fetchCurrent();
      onAfterApply?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setApplying(false);
    }
  }, [fetchCurrent, onAfterApply]);

  const canonicalIds = current?.canonical_keystone_stages?.map((s) => s.id) || [];
  const currentIds = current?.doc?.stages?.map((s) => s.id) || [];
  const aligned = canonicalIds.length > 0 && JSON.stringify(canonicalIds) === JSON.stringify(currentIds);

  return (
    <Card className="mb-4 border-blue-200" data-testid="pipeline-stages-card">
      <CardHeader>
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center justify-between w-full text-left" data-testid="pipeline-stages-toggle">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              CRM · Pipeline stages config
            </CardTitle>
            <CardDescription>
              Set the global <code>pipeline_config</code> doc to the canonical Keystone 7-stage list. Idempotent.
            </CardDescription>
          </div>
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800 flex items-start gap-2">
              <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          {loading && <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading current config…</div>}

          {current && (
            <>
              <div className="text-xs text-slate-600 border border-slate-200 rounded p-3 bg-slate-50">
                {current.note}
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="border border-slate-200 rounded p-3 bg-white">
                  <div className="text-xs font-semibold text-slate-700 mb-2">
                    Currently in DB · <code>pipeline_config</code>
                  </div>
                  {current.doc_present ? (
                    <ul className="text-xs space-y-1" data-testid="pipeline-stages-current-list">
                      {current.doc?.stages?.map((s) => (
                        <li key={s.id} className="flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
                          <code className="font-mono">{s.id}</code>
                          <span className="text-slate-500">— {s.name} (order {s.order})</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs text-amber-700">
                      No doc — app is falling back to <code>DEFAULT_PIPELINE_STAGES</code> baked in <code>routes/crm.py</code>.
                    </div>
                  )}
                </div>

                <div className="border border-blue-200 rounded p-3 bg-blue-50">
                  <div className="text-xs font-semibold text-blue-900 mb-2">
                    Canonical Keystone (would write)
                  </div>
                  <ul className="text-xs space-y-1" data-testid="pipeline-stages-canonical-list">
                    {current.canonical_keystone_stages?.map((s) => (
                      <li key={s.id} className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
                        <code className="font-mono">{s.id}</code>
                        <span className="text-slate-600">— {s.name} (order {s.order})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {aligned ? (
                <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
                  Already aligned — pipeline_config matches the canonical list. No write needed.
                </div>
              ) : (
                <Button
                  onClick={apply}
                  disabled={applying}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="pipeline-stages-apply-btn"
                >
                  {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Set to canonical Keystone stages
                </Button>
              )}
            </>
          )}

          {result && (
            <details>
              <summary className="cursor-pointer text-xs text-slate-600">Last apply result (JSON)</summary>
              <pre className="mt-1 p-2 bg-slate-900 text-slate-100 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(result, null, 2)}
              </pre>
            </details>
          )}
        </CardContent>
      )}
    </Card>
  );
}


function MigrationDriftCard({ onAfterApply }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [token, setToken] = useState('');
  const [reasons, setReasons] = useState({});  // {name: reason}
  const [amending, setAmending] = useState({}); // {name: true}

  const fetchDrift = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/diagnostics/migration-drift`, { headers: authHeaders() });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open && !data) fetchDrift(); }, [open, data, fetchDrift]);

  const amend = useCallback(async (name) => {
    const reason = (reasons[name] || '').trim();
    if (!reason) { toast.error('Reason is required'); return; }
    if (!window.confirm(`Amend the tracker checksum for ${name} to match the current on-disk file?\n\nReason: ${reason}\n\nThis records an audit trail (_checksum_amended_at/by/from/reason) so future debugging knows why the checksum changed. Only do this if the file change is pure docs/comments/whitespace.`)) return;
    setAmending((m) => ({ ...m, [name]: true }));
    setError(null);
    try {
      const res = await axios.post(`${API}/api/admin/diagnostics/migration-drift/amend`, null, {
        headers: { ...authHeaders(), 'X-Admin-Migrations-Token': token },
        params: { name, reason },
      });
      toast.success(`Amended ${name}: ${res.data.from_checksum} → ${res.data.to_checksum}`);
      await fetchDrift();
      onAfterApply?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setAmending((m) => ({ ...m, [name]: false }));
    }
  }, [reasons, token, fetchDrift, onAfterApply]);

  return (
    <Card className="mb-4 border-amber-200" data-testid="migration-drift-card">
      <CardHeader>
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center justify-between w-full text-left" data-testid="migration-drift-toggle">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Migrations · drift inspector
            </CardTitle>
            <CardDescription>
              Lists every applied migration whose on-disk file differs from its tracker checksum. Inspect the file, then amend (docs-only) or revert (logic).
            </CardDescription>
          </div>
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800 flex items-start gap-2">
              <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          {loading && <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</div>}

          {data && data.drift_count === 0 && (
            <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
              No drift — every applied migration&apos;s on-disk file matches its tracker checksum.
            </div>
          )}

          {data && data.drift_count > 0 && (
            <>
              <div className="text-xs text-slate-600 border border-amber-200 rounded p-3 bg-amber-50">
                {data.amend_instructions}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Cleanup token (required to amend)</label>
                <input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="StrayTenant cleanup token (same as the cleanup card)"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono"
                  data-testid="migration-drift-token-input"
                />
              </div>
              <ul className="space-y-3">
                {data.items.map((it) => (
                  <li key={it.name} className="border border-slate-200 rounded p-3 bg-white text-xs" data-testid={`migration-drift-row-${it.name}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={STATUS_BADGE_CLS.amber}>drifted</Badge>
                      <code className="font-mono font-semibold">{it.name}</code>
                      <span className="text-slate-500">size {it.file_size_bytes}b · {it.line_count} lines · {it.logic_line_count} logic lines</span>
                    </div>
                    <div className="text-slate-700 font-mono mb-2">
                      tracker:&nbsp;<code>{it.recorded_checksum}</code>{' → '}
                      on-disk:&nbsp;<code>{it.on_disk_checksum}</code>
                      <span className="ml-2 text-slate-500">(applied {it.applied_at})</span>
                    </div>
                    <details className="mb-2">
                      <summary className="cursor-pointer text-slate-600 text-[11px]">View file content ({it.line_count} lines)</summary>
                      <pre className="mt-1 p-2 bg-slate-900 text-slate-100 rounded text-[10px] overflow-x-auto whitespace-pre max-h-96">
{it.file_content}
                      </pre>
                    </details>
                    <details className="mb-2">
                      <summary className="cursor-pointer text-slate-600 text-[11px]">Logic-only lines preview (no comments/blanks)</summary>
                      <pre className="mt-1 p-2 bg-slate-50 text-slate-800 rounded text-[10px] overflow-x-auto whitespace-pre max-h-72 border border-slate-200">
{it.logic_lines_preview.join('\n')}
                      </pre>
                    </details>
                    <div className="border-t border-slate-200 pt-2 space-y-2">
                      <label className="block text-[11px] font-semibold text-slate-700">Amend reason (required, audit-logged)</label>
                      <input
                        type="text"
                        value={reasons[it.name] || ''}
                        onChange={(e) => setReasons((r) => ({ ...r, [it.name]: e.target.value }))}
                        placeholder='e.g. "docstring typo fix, no logic change"'
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
                        data-testid={`migration-drift-reason-${it.name}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => amend(it.name)}
                        disabled={!token || !(reasons[it.name] || '').trim() || amending[it.name]}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`migration-drift-amend-${it.name}`}
                      >
                        {amending[it.name] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                        Amend checksum
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}



/**
 * Security · cross-tenant denial audit log (Diagnostics Phase 3).
 *
 * Surfaces recent `company_update_denied` rows from activity_logs — the
 * audit trail written by routes/auth.py:update_company when a user
 * tries to patch another tenant's company doc. The request itself was
 * refused at the route (403) BEFORE any data could leak; this card is
 * monitoring/forensics only.
 */
function SecurityDenialsCard() {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDenials = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/security-denials?limit=10`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open && !data && !loading && !error) fetchDenials(); }, [open, data, loading, error, fetchDenials]);

  const total = data?.total || 0;
  const rows = data?.rows || [];

  return (
    <Card className="mb-4 border-rose-200" data-testid="security-denials-card">
      <CardHeader>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center justify-between w-full text-left"
          data-testid="security-denials-toggle"
        >
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Security · cross-tenant denials
              {total > 0 && (
                <Badge className={STATUS_BADGE_CLS.red} data-testid="security-denials-count-badge">
                  {total}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              <code>company_update_denied</code> audit rows — blocked attempts to PATCH another tenant&apos;s
              company doc. Requests were refused before any data could leak; this is forensics only.
            </CardDescription>
          </div>
        </button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchDenials}
              disabled={loading}
              data-testid="security-denials-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            {data && (
              <span className="text-xs text-slate-500">
                Showing {rows.length} of {total} total
              </span>
            )}
          </div>

          {error && (
            <div className="p-3 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800 flex items-start gap-2">
              <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          {data && rows.length === 0 && (
            <div
              className="p-3 rounded bg-emerald-50 border border-emerald-200 text-sm text-emerald-800"
              data-testid="security-denials-empty"
            >
              No cross-tenant attempts recorded.
            </div>
          )}

          {data && rows.length > 0 && (
            <ul className="space-y-2" data-testid="security-denials-list">
              {rows.map((r, i) => (
                <li
                  key={`${r.timestamp}-${i}`}
                  className="p-3 rounded border border-rose-200 bg-rose-50 text-xs"
                  data-testid={`security-denial-row-${i}`}
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge className={STATUS_BADGE_CLS.red}>denied</Badge>
                    <span className="font-mono text-slate-700">
                      {(r.timestamp || '').slice(0, 19).replace('T', ' ')}
                    </span>
                  </div>
                  <div className="text-slate-700">
                    <div>
                      <span className="font-semibold">Attacker:</span>{' '}
                      <code>{r.attacker_email || r.user_id || '(unknown)'}</code>
                    </div>
                    <div>
                      <span className="font-semibold">Their tenant:</span>{' '}
                      <code>{r.company_id || '(none)'}</code>
                    </div>
                    <div>
                      <span className="font-semibold">Target company:</span>{' '}
                      <code>{r.entity_id || '(unknown)'}</code>
                    </div>
                    {r.description && (
                      <div className="mt-1 text-slate-600 font-mono text-[11px]">
                        {r.description}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
}
